import { initializeApp, type FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithCustomToken, 
  signInWithRedirect,
  getRedirectResult,
  SAMLAuthProvider,
  onAuthStateChanged, 
  type Auth, 
  type User 
} from 'firebase/auth';
import { getFirestore, setLogLevel, type Firestore } from 'firebase/firestore';
import { firebaseConfig } from './config';

// IMPORTANT: Use setLogLevel('Debug') for better debugging
setLogLevel('debug');

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let auth: Auth | null = null;
let userId: string | null = null;
let isAuthReady = false;
let isRedirecting = false; // Flag to prevent multiple redirect attempts

export function initializeFirebase(): { app: FirebaseApp; db: Firestore; auth: Auth } {
  if (app && db && auth) {
    return { app, db, auth };
  }

  app = initializeApp(firebaseConfig);
  db = getFirestore(app);
  auth = getAuth(app);

  return { app, db, auth };
}

export async function setupAuth(initialAuthToken?: string): Promise<void> {
  if (!auth) {
    throw new Error('Auth not initialized. Call initializeFirebase() first.');
  }

  // Prevent multiple redirect attempts
  if (isRedirecting) {
    console.log('SAML redirect already in progress, waiting...');
    return;
  }

  // First, check if we're returning from a SAML redirect
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      // User successfully authenticated via SAML redirect
      userId = result.user.uid;
      isAuthReady = true;
      isRedirecting = false;
      return;
    }
  } catch (error: any) {
    console.error('Error getting redirect result:', error);
    isRedirecting = false;
    
    // Handle specific SAML configuration errors
    if (error?.code === 'auth/app-not-configured' || error?.code === 'auth/operation-not-allowed') {
      throw new Error(
        'SAML provider not configured. Please ensure:\n' +
        '1. Identity Platform is enabled in Firebase Console\n' +
        '2. SAML provider "saml.dhsb" is configured in Authentication > Sign-in method\n' +
        '3. Your domain is authorized in the SAML provider settings'
      );
    }
    if (error?.code === 'auth/app-not-configured-for-user' || error?.message?.includes('app_not_configured_for_user')) {
      throw new Error(
        'SAML authentication failed: Your account is not authorized for this application.\n' +
        'Please contact your administrator to ensure your email domain is authorized in the SAML provider settings.'
      );
    }
    // Don't re-throw here - allow auth state listener to handle it
  }

  // Wait for auth state to be ready before checking current user
  // This ensures Firebase Auth has finished initializing
  return new Promise((resolve, reject) => {
    if (!auth) {
      reject(new Error('Auth not initialized'));
      return;
    }

    // Set up a one-time listener to wait for auth state initialization
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      unsubscribe(); // Only listen once
      
      if (user) {
        // User is already authenticated
        userId = user.uid;
        isAuthReady = true;
        isRedirecting = false;
        resolve();
        return;
      }

      // If custom token provided, use it
      if (initialAuthToken && auth) {
        try {
          await signInWithCustomToken(auth, initialAuthToken);
          resolve();
          return;
        } catch (error) {
          console.error('Error signing in with custom token:', error);
          reject(error);
          return;
        }
      }

      // No user and no custom token - initiate SAML authentication
      // Only redirect if we haven't already initiated one
      if (!isRedirecting) {
        isRedirecting = true;
        try {
          await signInWithSAML();
          // Note: signInWithRedirect will cause page navigation, so resolve won't be called
          // But we set the flag to prevent multiple attempts
        } catch (error) {
          isRedirecting = false;
          reject(error);
        }
      } else {
        resolve(); // Already redirecting, just resolve
      }
    });
  });
}

/**
 * Signs in with SAML provider using the provider ID "saml.dhsb"
 */
export async function signInWithSAML(): Promise<void> {
  if (!auth) {
    throw new Error('Auth not initialized. Call initializeFirebase() first.');
  }

  try {
    const samlProvider = new SAMLAuthProvider('saml.dhsb');
    await signInWithRedirect(auth, samlProvider);
    // Note: signInWithRedirect will cause a page navigation, so code after this won't execute
  } catch (error: any) {
    console.error('SAML sign-in error:', error);
    
    // Provide helpful error messages for common issues
    if (error?.code === 'auth/operation-not-allowed') {
      throw new Error(
        'SAML provider is not enabled. Please enable it in Firebase Console:\n' +
        'Authentication > Sign-in method > SAML > Enable'
      );
    }
    if (error?.code === 'auth/invalid-provider-id') {
      throw new Error(
        'Invalid SAML provider ID. Please verify that "saml.dhsb" is correctly configured in Firebase Console.'
      );
    }
    
    throw error;
  }
}

export function getUserId(): string | null {
  return userId;
}

export function getIsAuthReady(): boolean {
  return isAuthReady;
}

export function getDb(): Firestore | null {
  return db;
}

export function getAuthInstance(): Auth | null {
  return auth;
}

